import { useNavigate } from "react-router-dom";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Grid, Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FormField from "components/FormField/FormField";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import MDDatePicker from "components/MDDatePicker";
import { capitalCase } from "change-case";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import { DailyFormIssuePriority, UserStatus } from "generated/graphql";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import {
  updateSchema,
  createSchema,
  getDefaultValues,
  UpdateWorkOrderInput,
  CreateWorkOrderInput,
} from "DDD/action-objects/WorkOrderEdit";
import useGetDailyFormVehicles from "hooks/fleet/useGetDailyFormVehicles";
import AddFilesToWorkOrder from "./AddFilesToWorkOrder";
import dayjs from "dayjs";
import { workOrderStatusOptions } from "modules/fleet/workOrderStatusOptions";

interface WorkOrderFormProps {
  workOrder?: any;
  onSubmit: SubmitHandler<UpdateWorkOrderInput | CreateWorkOrderInput>;
  loading: boolean;
  mode?: "create" | "edit";
  onCancel?: () => void;
}

export default function WorkOrderForm({
  workOrder,
  onSubmit,
  loading,
  mode = "edit",
  onCancel,
}: WorkOrderFormProps) {
  const navigate = useNavigate();

  const formSchema = mode === "create" ? createSchema : updateSchema;
  const defaultValues = getDefaultValues(workOrder);

  const [fileData, setFileData] = useState<{
    newFiles: { name: string; path: string }[];
    removedFileIds: string[];
  }>({
    newFiles: [],
    removedFileIds: [],
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UpdateWorkOrderInput | CreateWorkOrderInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const [getDailyFormVehicles, { data: vehicles, loading: loadingVehicles }] =
    useGetDailyFormVehicles();

  useEffect(() => {
    getDailyFormVehicles({ first: 1000, page: 1 });
  }, [getDailyFormVehicles]);

  const priorityOptions = enumToValueOptions(DailyFormIssuePriority);
  const statusOptions = workOrderStatusOptions.map((option) => ({
    value: option.value,
    label: option.label,
  }));
  const issueTypeOptions = [
    { value: "Something is broken", label: "Something is broken" },
    { value: "Maintenance Item", label: "Maintenance Item" },
    { value: "Equipment update", label: "Equipment update" },
    { value: "Wishlist", label: "Wishlist" },
  ];

  const vehicleOptions =
    vehicles
      ?.map((vehicle) => ({
        value: vehicle.id,
        label: vehicle.name,
        category: vehicle.dailyFormVehicleType?.name
          ? capitalCase(vehicle.dailyFormVehicleType.name)
          : "Other",
      }))
      .sort((a, b) => (a.category && b.category ? a.category.localeCompare(b.category) : 0)) || [];

  const handleFormSubmit = (data: UpdateWorkOrderInput | CreateWorkOrderInput) => {
    const submitData = {
      ...data,
      files: fileData.newFiles.length > 0 ? fileData.newFiles : undefined,
      ...(mode === "edit" &&
        fileData.removedFileIds.length > 0 && {
          removeFiles: fileData.removedFileIds,
        }),
    };
    onSubmit(submitData);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/fleet/work-orders");
    }
  };

  return (
    <Card>
      <MDBox p={3}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormField
                type="text"
                label="Issue Name"
                placeholder="Issue Name"
                error={errors.issueName}
                {...register("issueName", { required: "Issue name is required" })}
                maxWidth="400px"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="vehicleId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AutoComplete
                    field={field}
                    options={vehicleOptions}
                    loading={loadingVehicles}
                    error={error}
                    label="Vehicle"
                    variant="standard"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomSelect
                control={control}
                name="issueType"
                data={issueTypeOptions}
                label="Issue Type"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomSelect
                control={control}
                name="priority"
                data={priorityOptions}
                label="Priority"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomSelect
                control={control}
                name="status"
                data={statusOptions}
                label="Status"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <OrganizationUsersOptions
                queryOverride={{ first: 1000, status: UserStatus.ACTIVE }}
                archivedUser={workOrder?.assignedTo}
              >
                {({ organizationUsersOptions, loading: loadingUsers, isOptionDisabled }) => (
                  <Controller
                    name="assignedTo"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <AutoComplete
                        field={field}
                        options={organizationUsersOptions}
                        loading={loadingUsers}
                        error={error}
                        label="Assign To"
                        variant="standard"
                        getOptionDisabled={isOptionDisabled}
                      />
                    )}
                  />
                )}
              </OrganizationUsersOptions>
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="neededCompletionDate"
                control={control}
                render={({ field: { onChange, value, ...fieldProps } }) => (
                  <MDDatePicker
                    {...fieldProps}
                    value={value ? new Date(value) : null}
                    onChange={(selectedDates) => {
                      const date = selectedDates?.[0];
                      onChange(date ? dayjs(date).format("YYYY-MM-DD") : null);
                    }}
                    input={{
                      label: "Needed Completion Date",
                      InputLabelProps: { shrink: true },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField
                type="number"
                label="Current Meter"
                placeholder="Current Meter"
                error={errors.currentMeterReading}
                inputProps={{ step: "0.1", min: "0" }}
                {...register("currentMeterReading", {
                  setValueAs: (value) => (value === "" ? null : Number(value)),
                })}
                maxWidth="400px"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField
                type="number"
                label="Estimated Task Time (Hrs)"
                placeholder="Estimated Task Time (Hrs)"
                error={errors.estimatedTaskTime}
                inputProps={{ step: "0.1", min: "0" }}
                {...register("estimatedTaskTime", {
                  setValueAs: (value) => (value === "" ? null : Number(value)),
                })}
                maxWidth="400px"
              />
            </Grid>

            <Grid item xs={12}>
              <FormField
                type="text"
                label="Description"
                placeholder="Description"
                multiline
                rows={4}
                variant="outlined"
                error={errors.issueDescription}
                {...register("issueDescription")}
                maxWidth="100%"
              />
            </Grid>

            <Grid item xs={12}>
              <FormField
                type="text"
                label="Shop Comments"
                placeholder="Comments"
                multiline
                rows={3}
                variant="outlined"
                error={errors.comments}
                {...register("comments")}
                maxWidth="100%"
              />
            </Grid>

            <Grid item xs={12}>
              <AddFilesToWorkOrder
                onFilesChange={setFileData}
                existingFiles={workOrder?.files || []}
              />
            </Grid>

            <Grid item xs={12}>
              <MDBox display="flex" gap={2}>
                <MDButton variant="gradient" color="success" type="submit" disabled={loading}>
                  {mode === "create" ? "Create Shop Task" : "Save Changes"}
                </MDButton>
                <MDButton variant="outlined" color="secondary" onClick={handleCancel} type="button">
                  Cancel
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </form>
      </MDBox>
    </Card>
  );
}
